import React, { useEffect, useState, useCallback } from "react";
import { Bell, CheckCheck } from "lucide-react";
import { api, errorMessage } from "../lib/api";
import { toast } from "sonner";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import { Button } from "../components/ui/button";
import { fmtDateTime } from "../lib/constants";

const ROLE_PATHS = {
  sponsor: {
    list: "/sponsor/notifications",
    read: (id) => `/sponsor/notifications/${id}/read`,
    readAll: "/sponsor/notifications/read-all",
    target: (appId) => `/broker/applications/${appId}`,
  },
  admin: {
    list: "/admin/notifications",
    read: (id) => `/admin/notifications/${id}/read`,
    readAll: "/admin/notifications/read-all",
    target: (appId) => `/admin/applications?highlight=${appId}`,
  },
  borrower: {
    list: "/borrower/notifications",
    read: (id) => `/borrower/notifications/${id}/read`,
    readAll: null,
    target: () => "/borrower",
  },
};

export default function NotificationBell({ role, onNavigate }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const cfg = ROLE_PATHS[role] || ROLE_PATHS.borrower;

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(cfg.list);
      setItems(data || []);
    } catch {
      // ignore
    }
  }, [cfg.list]);

  useEffect(() => {
    load();
    const i = setInterval(load, 30000);
    return () => clearInterval(i);
  }, [load]);

  const unread = items.filter((n) => !n.read).length;

  const onClickItem = async (n) => {
    try {
      await api.post(cfg.read(n.notification_id));
    } catch {}
    setOpen(false);
    await load();
    if (n.application_id && onNavigate) {
      onNavigate(cfg.target(n.application_id), n.application_id);
    }
  };

  const markAll = async () => {
    if (!cfg.readAll) return;
    try {
      await api.post(cfg.readAll);
      await load();
      toast.success("All notifications marked as read");
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="relative inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-800 bg-slate-900 text-slate-200 hover:bg-slate-800 transition-colors"
          data-testid="notification-bell-btn"
          aria-label="Notifications"
        >
          <Bell className="h-4 w-4" />
          {unread > 0 && (
            <span
              className="absolute -top-1 -right-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-emerald-500 px-1 text-[10px] font-semibold text-slate-950 tabular-nums"
              data-testid="notification-unread-count"
            >
              {unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-96 border-slate-800 bg-slate-900 text-slate-100 p-0"
      >
        <div className="flex items-center justify-between border-b border-slate-800 px-4 py-3">
          <div className="text-sm font-semibold">Notifications</div>
          {cfg.readAll && (
            <Button
              variant="ghost"
              size="sm"
              onClick={markAll}
              className="h-7 text-xs text-slate-300 hover:text-emerald-300"
              data-testid="mark-all-read-btn"
            >
              <CheckCheck className="h-3.5 w-3.5 mr-1" />
              Mark all read
            </Button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 && (
            <div className="p-6 text-center text-sm text-slate-500">
              No notifications yet.
            </div>
          )}
          {items.map((n) => (
            <button
              key={n.notification_id}
              onClick={() => onClickItem(n)}
              data-testid={`notification-item-${n.notification_id}`}
              className={`w-full border-b border-slate-800 px-4 py-3 text-left transition-colors hover:bg-slate-800/60 ${
                !n.read ? "bg-slate-800/30" : ""
              }`}
            >
              <div className="flex items-start gap-2">
                {!n.read && (
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-400" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-slate-100">{n.title}</div>
                  <div className="mt-0.5 text-xs text-slate-400 line-clamp-2">
                    {n.message}
                  </div>
                  <div className="mt-1 text-[11px] text-slate-500">
                    {fmtDateTime(n.created_at)}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
